// apps/web/src/pages/compras/pedido-compra-detalhe-page.tsx — Fase 13, Plano 19 (COMP-02, COMP-04, D-14, D-16, D-19).
// Detalhe do pedido de compra: cabeçalho em Card (número, situação, fornecedor, data, prazo geral, total, responsável) e
// abas Itens · NFs vinculadas · Histórico (aba na URL). Recebido, saldo a receber, excedente, situação e atraso vêm do
// servidor, derivados dos recebimentos: nada é digitado nem recalculado aqui (COMP-04). "Baixar PDF do pedido" fica em
// todas as situações (o usuário envia o PDF ao fornecedor por fora; sem e-mail nesta fase). Cancelar pedido e encerrar
// saldo de item pedem motivo (D-19) e só aparecem para quem tem compras:gerenciar.
import { Fragment, useCallback, useEffect, useRef, useState, type ComponentType } from 'react';
import {
  AlertTriangle,
  Archive,
  CalendarX,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Clock,
  FileDown,
  Loader2,
  MoreHorizontal,
  PackageOpen,
  Pencil,
  RefreshCw,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import type { HistoricoPedidoCompra, PedidoCompraItemView, PedidoCompraView, SituacaoNotaCompra } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../../components/ui/breadcrumb';
import { formatarCnpj } from '../../lib/compras/cnpj';
import { baixarArquivo } from '../../lib/compras/download';
import { urlConferencia } from '../../lib/compras/importacao-xml';
import { itemEditavel } from '../../lib/compras/pedido-form';
import { rotuloSituacaoNota } from '../../lib/compras/notas';
import {
  ABAS_DETALHE_PEDIDO,
  abaDoPedidoDaUrl,
  apresentacaoSituacaoItem,
  apresentacaoSituacaoPedido,
  itemEncerrado,
  prazoDoItem,
  saldoExibido,
  textoAtraso,
  type AbaDetalhePedido,
  type IconeSituacaoItem,
  type IconeSituacaoPedido,
} from '../../lib/compras/pedidos';
import { faixaUrgencia } from '../../lib/kanban/apresentacao';
import { obterSessaoAtual } from '../../lib/session';
import { formatarData, formatarDataHora, formatarMoeda, formatarQuantidade } from '../../lib/vendas/formatacao';
import { CancelarPedidoDialog, EncerrarSaldoDialog } from './pedido-compra-dialogs';

type IconeLucide = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

const ICONES_SITUACAO: Record<IconeSituacaoPedido | IconeSituacaoItem, IconeLucide> = { CircleDashed, PackageOpen, CheckCircle2, XCircle, Archive };

const ROTULOS_ABA: Record<AbaDetalhePedido, string> = { itens: 'Itens', nfs: 'NFs vinculadas', historico: 'Histórico' };

const CABECALHO_COLUNA = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const MENSAGEM_FALHA_PDF = 'Não foi possível gerar o PDF do pedido. Tente novamente.';

function Campo({ rotulo, className, children }: { rotulo: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{rotulo}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function BotaoBaixarPdf({ gerando, onClick }: { gerando: boolean; onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick} disabled={gerando} aria-busy={gerando}>
      {gerando ? <Loader2 className="animate-spin" aria-hidden="true" /> : <FileDown aria-hidden="true" />}
      {gerando ? 'Gerando PDF…' : 'Baixar PDF do pedido'}
    </Button>
  );
}

function SituacaoItemBadge({ item }: { item: PedidoCompraItemView }) {
  const situacao = apresentacaoSituacaoItem(item.situacao);
  const Icone = ICONES_SITUACAO[situacao.icone];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
      <Icone className="size-3.5 shrink-0" aria-hidden="true" />
      {situacao.rotulo}
    </span>
  );
}

/** Data do prazo + faixa de urgência: amber perto (Clock), rose atrasado (CalendarX "Atrasado N dia(s)"). */
function PrazoItem({ item }: { item: PedidoCompraItemView }) {
  const prazo = prazoDoItem(item);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="tabular-nums">{formatarData(item.prazoEntrega)}</span>
      {prazo === 'estourado' && item.atrasoDias !== null && item.atrasoDias > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-rose-700">
          <CalendarX className="size-3.5 shrink-0" aria-hidden="true" />
          {textoAtraso(item.atrasoDias)}
        </span>
      )}
      {prazo === 'perto' && (
        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
          <Clock className="size-3.5 shrink-0" aria-hidden="true" />
          Prazo próximo
        </span>
      )}
    </div>
  );
}

interface AcoesItem {
  readonly podeGerenciar: boolean;
  readonly pedidoCancelado: boolean;
  readonly onEditar: (item: PedidoCompraItemView) => void;
  readonly onEncerrarSaldo: (item: PedidoCompraItemView) => void;
}

/** "Encerrar saldo" só faz sentido para item aberto/parcial que ainda tem saldo a receber. */
function podeEncerrarSaldo(item: PedidoCompraItemView): boolean {
  return !item.saldoEncerrado && (item.situacao === 'ABERTO' || item.situacao === 'PARCIAL') && item.saldoAReceber > 0;
}

function LinhaItem({ item, expandido, onAlternar, acoes }: { item: PedidoCompraItemView; expandido: boolean; onAlternar: () => void; acoes: AcoesItem }) {
  const apagado = itemEncerrado(item);
  const temExcedente = item.excedente > 0;
  const prazo = prazoDoItem(item);
  const idDetalhe = `pc-item-detalhe-${item.id}`;
  const mostrarAcoes = acoes.podeGerenciar && !acoes.pedidoCancelado && !apagado && itemEditavel(item);
  return (
    <Fragment>
      <tr className={`border-t ${apagado ? 'text-muted-foreground' : ''}`}>
        <td className="relative max-w-[22rem] py-2 pl-5 pr-3">
          <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1.5 ${faixaUrgencia({ nivel: 'normal', prazo })}`} />
          <div className="truncate font-semibold" title={item.materialNome}>
            {item.materialCodigo}
          </div>
          <div className="truncate text-muted-foreground">{item.materialNome}</div>
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatarQuantidade(item.quantidade)} {item.unidadeCompra}
          {item.quantidadeEstoquePrevista !== null && (
            <div className="text-xs text-muted-foreground">
              = {formatarQuantidade(item.quantidadeEstoquePrevista)} {item.unidadeEstoque}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {formatarQuantidade(item.recebido)} {item.unidadeCompra}
          {temExcedente && (
            <div>
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                <TrendingUp className="size-3.5 shrink-0" aria-hidden="true" />+{formatarQuantidade(item.excedente)} a mais
              </span>
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right font-semibold tabular-nums">
          {formatarQuantidade(saldoExibido(item))} {item.unidadeCompra}
        </td>
        <td className="hidden px-3 py-2 text-right tabular-nums xl:table-cell">{formatarMoeda(item.precoUnitario, 4)}</td>
        <td className="hidden px-3 py-2 xl:table-cell">
          <PrazoItem item={item} />
        </td>
        <td className="hidden px-3 py-2 xl:table-cell">
          <SituacaoItemBadge item={item} />
        </td>
        <td className="whitespace-nowrap px-1 py-1 text-right">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-expanded={expandido}
            aria-controls={idDetalhe}
            aria-label={`${expandido ? 'Recolher' : 'Expandir'} item ${item.numeroItem}`}
            className="size-11 sm:size-9"
            onClick={onAlternar}
          >
            {expandido ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          </Button>
          {mostrarAcoes && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label={`Mais ações do item ${item.numeroItem}`} className="size-11 sm:size-9">
                  <MoreHorizontal aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => acoes.onEditar(item)} className="min-h-10">
                  Editar item
                </DropdownMenuItem>
                {podeEncerrarSaldo(item) && (
                  <DropdownMenuItem onSelect={() => acoes.onEncerrarSaldo(item)} className="min-h-10">
                    Encerrar saldo
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </td>
      </tr>
      {expandido && (
        <tr id={idDetalhe} className="border-t bg-slate-50">
          <td colSpan={8} className="px-5 py-3">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div className="xl:hidden">
                <dt className="text-xs text-muted-foreground">Preço</dt>
                <dd className="tabular-nums">{formatarMoeda(item.precoUnitario, 4)}</dd>
              </div>
              <div className="xl:hidden">
                <dt className="text-xs text-muted-foreground">Prazo</dt>
                <dd>
                  <PrazoItem item={item} />
                </dd>
              </div>
              <div className="xl:hidden">
                <dt className="text-xs text-muted-foreground">Situação</dt>
                <dd>
                  <SituacaoItemBadge item={item} />
                </dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Código do fornecedor</dt>
                <dd>{item.codigoFornecedor ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Total do item</dt>
                <dd className="tabular-nums">{formatarMoeda(item.total)}</dd>
              </div>
              {item.saldoEncerrado && (
                <div className="col-span-2 sm:col-span-4">
                  <dt className="text-xs text-muted-foreground">Saldo encerrado</dt>
                  <dd className="whitespace-pre-wrap">
                    Faltaram {formatarQuantidade(item.saldoAReceber)} {item.unidadeCompra}
                    {item.saldoEncerradoMotivo ? `. Motivo: ${item.saldoEncerradoMotivo}` : '.'}
                  </dd>
                </div>
              )}
              {item.semFatorConversao && (
                <div className="col-span-2 sm:col-span-4">
                  <dd className="inline-flex items-center gap-1.5 text-amber-800">
                    <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
                    Sem fator de conversão: o recebimento fica bloqueado até cadastrar o fator.
                  </dd>
                </div>
              )}
            </dl>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function ItensAba({ pedido, acoes }: { pedido: PedidoCompraView; acoes: AcoesItem }) {
  const [expandidos, setExpandidos] = useState<ReadonlySet<string>>(new Set());
  function alternar(id: string): void {
    setExpandidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }
  if (pedido.itens.length === 0) {
    return (
      <Card>
        <p className="px-6 py-10 text-center text-sm text-muted-foreground">Nenhum item neste pedido.</p>
      </Card>
    );
  }
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Itens do pedido de compra {pedido.numero}</caption>
          <thead className="bg-slate-100 text-left">
            <tr>
              <th scope="col" className={CABECALHO_COLUNA}>Matéria-prima</th>
              <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Pedido</th>
              <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Recebido</th>
              <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Saldo a receber</th>
              <th scope="col" className={`${CABECALHO_COLUNA} hidden text-right xl:table-cell`}>Preço</th>
              <th scope="col" className={`${CABECALHO_COLUNA} hidden xl:table-cell`}>Prazo</th>
              <th scope="col" className={`${CABECALHO_COLUNA} hidden xl:table-cell`}>Situação</th>
              <th scope="col" className="px-3 py-2">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pedido.itens.map((item) => (
              <LinhaItem key={item.id} item={item} expandido={expandidos.has(item.id)} onAlternar={() => alternar(item.id)} acoes={acoes} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function NfsAba({ pedido }: { pedido: PedidoCompraView }) {
  if (pedido.notas.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
          <p className="text-sm font-medium">Nenhuma NF vinculada a este pedido ainda.</p>
          <p className="max-w-xl text-sm text-muted-foreground">Importe o XML da nota em Notas de Compra.</p>
        </div>
      </Card>
    );
  }
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">NFs vinculadas ao pedido {pedido.numero}</caption>
          <thead className="bg-slate-100 text-left">
            <tr>
              <th scope="col" className={CABECALHO_COLUNA}>NF</th>
              <th scope="col" className={CABECALHO_COLUNA}>Emissão</th>
              <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Valor</th>
              <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Itens vinculados</th>
              <th scope="col" className={CABECALHO_COLUNA}>Situação</th>
              <th scope="col" className="px-3 py-2">
                <span className="sr-only">Abrir conferência</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pedido.notas.map((nota) => (
              <tr key={nota.notaCompraId} className="border-t">
                <td className="px-3 py-2 font-medium tabular-nums">{nota.numero}</td>
                <td className="px-3 py-2 tabular-nums">{formatarData(nota.emissao)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(nota.valorTotal)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{nota.itensVinculados}</td>
                <td className="px-3 py-2">{rotuloSituacaoNota(nota.situacao as SituacaoNotaCompra)}</td>
                <td className="px-3 py-2 text-right">
                  <Button asChild variant="outline" size="sm">
                    <a href={urlConferencia(nota.notaCompraId)} aria-label={`Abrir conferência da NF ${nota.numero}`}>
                      Abrir conferência
                    </a>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Aba "Histórico": eventos auditados do pedido, mais recentes primeiro; só é buscada ao abrir (e a cada troca de página). */
function HistoricoAba({ pedidoId, versao }: { pedidoId: string; versao: number }) {
  const [pagina, setPagina] = useState(1);
  const [historico, setHistorico] = useState<HistoricoPedidoCompra | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(
    async (numeroPagina: number, sinal: { vivo: boolean }) => {
      setErro(false);
      setCarregando(true);
      try {
        const res = await fetch(`/api/v1/compras/pedidos/${encodeURIComponent(pedidoId)}/historico?pagina=${numeroPagina}`, { credentials: 'include' });
        if (!sinal.vivo) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const dados = (await res.json()) as HistoricoPedidoCompra;
        if (sinal.vivo) setHistorico(dados);
      } catch {
        if (sinal.vivo) setErro(true);
      } finally {
        if (sinal.vivo) setCarregando(false);
      }
    },
    [pedidoId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(pagina, sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar, pagina, versao]);

  if (erro) {
    return (
      <Card>
        <div role="alert" className="flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-rose-700">Não foi possível carregar o histórico. Verifique a conexão e tente novamente.</p>
          <Button type="button" variant="outline" onClick={() => void carregar(pagina, { vivo: true })}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  if (historico === null) {
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando histórico">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (historico.itens.length === 0 && pagina === 1) {
    return (
      <Card>
        <p className="px-6 py-10 text-center text-sm font-medium">Nenhum evento registrado ainda.</p>
      </Card>
    );
  }

  const totalPaginas = Math.max(1, Math.ceil(historico.total / Math.max(1, historico.tamanhoPagina)));

  return (
    <Card className="gap-0 overflow-hidden py-0" aria-busy={carregando}>
      <ol aria-label="Eventos do pedido de compra">
        {historico.itens.map((evento) => (
          <li key={evento.id} className="flex flex-col gap-1 border-t px-4 py-3 first:border-t-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-semibold">{evento.rotulo}</span>
              <span className="text-sm text-muted-foreground">{evento.actorNome ?? 'Sistema'}</span>
              <span className="ml-auto text-sm tabular-nums text-muted-foreground">{formatarDataHora(evento.createdAt)}</span>
            </div>
            {evento.justificativa && (
              <p className="whitespace-pre-wrap break-words text-sm">
                <span className="font-medium">Motivo: </span>
                {evento.justificativa}
              </p>
            )}
          </li>
        ))}
      </ol>
      {totalPaginas > 1 && (
        <nav aria-label="Paginação do histórico" className="flex items-center justify-between gap-3 border-t px-4 py-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPagina((atual) => Math.max(1, atual - 1))} disabled={pagina <= 1 || carregando}>
            <ChevronLeft aria-hidden="true" />
            Anterior
          </Button>
          <span className="inline-flex items-center gap-2 text-sm tabular-nums text-muted-foreground">
            {carregando && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Página {historico.pagina} de {totalPaginas}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))} disabled={pagina >= totalPaginas || carregando}>
            Próxima
            <ChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </Card>
  );
}

// O router escuta `popstate`; despachar o evento é a navegação programática sem reload (mesmo padrão do PV).
function navegar(destino: string): void {
  window.history.pushState(null, '', destino);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function PedidoCompraDetalhePage() {
  const id = new URLSearchParams(window.location.search).get('id');
  const criadoAgora = useRef(new URLSearchParams(window.location.search).get('criado') === '1');
  const [pedido, setPedido] = useState<PedidoCompraView | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<'carga' | 'nao-encontrado' | null>(null);
  const [aba, setAba] = useState<AbaDetalhePedido>(() => abaDoPedidoDaUrl(window.location.search));
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);
  const [bannerCriado, setBannerCriado] = useState(criadoAgora.current);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [toastFalha, setToastFalha] = useState<string | null>(null);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [itemEncerrando, setItemEncerrando] = useState<string | null>(null);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // O parâmetro `criado=1` só serve para mostrar o aviso uma vez: recarregar a página não repete "Pedido criado.".
  useEffect(() => {
    if (!criadoAgora.current) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('criado');
    window.history.replaceState(null, '', `${url.pathname}${url.search}`);
  }, []);

  // Toast de falha genérica (padrão do detalhe do PV): some sozinho em 5 s.
  useEffect(() => {
    if (!toastFalha) return;
    const timer = window.setTimeout(() => setToastFalha(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toastFalha]);

  // Voltar/avançar do navegador restaura a aba (deep-link `&aba=`).
  useEffect(() => {
    const aoNavegar = () => setAba(abaDoPedidoDaUrl(window.location.search));
    window.addEventListener('popstate', aoNavegar);
    return () => window.removeEventListener('popstate', aoNavegar);
  }, []);

  function trocarAba(proxima: string): void {
    const nova = (ABAS_DETALHE_PEDIDO as readonly string[]).includes(proxima) ? (proxima as AbaDetalhePedido) : 'itens';
    setAba(nova);
    const url = new URL(window.location.href);
    url.searchParams.set('aba', nova);
    window.history.pushState(null, '', `${url.pathname}${url.search}`);
  }

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }, silencioso = false): Promise<PedidoCompraView | null> => {
      if (!id) {
        setErro('nao-encontrado');
        setCarregando(false);
        return null;
      }
      if (!silencioso) setCarregando(true);
      setErro(null);
      try {
        const res = await fetch(`/api/v1/compras/pedidos/${encodeURIComponent(id)}`, { credentials: 'include' });
        if (!montado.current || (sinal && !sinal.vivo)) return null;
        if (res.status === 404 || res.status === 400) {
          setErro('nao-encontrado');
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const atual = (await res.json()) as PedidoCompraView;
        if (!montado.current || (sinal && !sinal.vivo)) return null;
        setPedido(atual);
        return atual;
      } catch {
        if (montado.current && (!sinal || sinal.vivo)) {
          if (silencioso) setToastFalha('Não foi possível recarregar o pedido de compra. Tente novamente.');
          else setErro('carga');
        }
        return null;
      } finally {
        if (!silencioso && montado.current && (!sinal || sinal.vivo)) setCarregando(false);
      }
    },
    [id],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  async function baixarPdf(): Promise<void> {
    if (!pedido || gerandoPdf) return;
    setGerandoPdf(true);
    try {
      await baixarArquivo(`/api/v1/compras/pedidos/${encodeURIComponent(pedido.id)}/pdf`, `${pedido.numero}.pdf`);
    } catch {
      if (montado.current) setToastFalha(MENSAGEM_FALHA_PDF);
    } finally {
      if (montado.current) setGerandoPdf(false);
    }
  }

  if (carregando) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-6" aria-busy="true" aria-label="Carregando pedido de compra">
        <Skeleton className="h-5 w-64" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (erro || !pedido) {
    return (
      <div className="mx-auto w-full max-w-7xl p-6">
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">
              {erro === 'nao-encontrado'
                ? 'Pedido de compra não encontrado.'
                : 'Não foi possível carregar o pedido de compra. Verifique a conexão e tente novamente.'}
            </p>
            {erro === 'carga' ? (
              <Button type="button" variant="outline" onClick={() => void carregar()}>
                <RefreshCw aria-hidden="true" />
                Tentar novamente
              </Button>
            ) : (
              <Button asChild variant="outline">
                <a href="/compras/pedidos">Voltar</a>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const situacao = apresentacaoSituacaoPedido(pedido.situacao);
  const IconeSituacao = ICONES_SITUACAO[situacao.icone];
  const podeGerenciar = permissoes.includes('compras:gerenciar');
  const pedidoCancelado = pedido.cancelado || pedido.situacao === 'CANCELADO';
  const editarPedido = (): void => navegar(`/compras/pedidos/novo?id=${encodeURIComponent(pedido.id)}`);
  const acoesItem: AcoesItem = {
    podeGerenciar,
    pedidoCancelado,
    onEditar: (item) => navegar(`/compras/pedidos/novo?id=${encodeURIComponent(pedido.id)}&item=${encodeURIComponent(item.id)}`),
    onEncerrarSaldo: (item) => setItemEncerrando(item.id),
  };
  const itemParaEncerrar = itemEncerrando ? (pedido.itens.find((candidato) => candidato.id === itemEncerrando) ?? null) : null;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <Breadcrumb aria-label="Trilha">
        <BreadcrumbList>
          <BreadcrumbItem>Compras</BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href="/compras/pedidos">Pedidos de Compra</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{pedido.numero}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {bannerCriado && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
          <span className="font-medium">Pedido {pedido.numero} criado.</span>
          <BotaoBaixarPdf gerando={gerandoPdf} onClick={() => void baixarPdf()} />
          <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={() => setBannerCriado(false)}>
            Dispensar
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold leading-tight tabular-nums">{pedido.numero}</h1>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${situacao.classes}`}>
              <IconeSituacao className="size-3 shrink-0" aria-hidden="true" />
              {situacao.rotulo}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <BotaoBaixarPdf gerando={gerandoPdf} onClick={() => void baixarPdf()} />
              {podeGerenciar && !pedidoCancelado && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="icon" aria-label={`Mais ações do pedido ${pedido.numero}`} className="size-11 sm:size-9">
                      <MoreHorizontal aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={editarPedido} className="min-h-10">
                      <Pencil aria-hidden="true" />
                      Editar pedido
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setCancelarAberto(true)} className="min-h-10">
                      <XCircle aria-hidden="true" />
                      Cancelar pedido
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Campo rotulo="Fornecedor">
              {pedido.fornecedorRazaoSocial}
              <div className="text-xs text-muted-foreground tabular-nums">{formatarCnpj(pedido.fornecedorCnpj)}</div>
            </Campo>
            <Campo rotulo="Data">
              <span className="tabular-nums">{formatarData(pedido.data)}</span>
            </Campo>
            <Campo rotulo="Prazo geral">
              <span className="tabular-nums">{formatarData(pedido.prazoGeral)}</span>
            </Campo>
            <Campo rotulo="Total">
              <span className="tabular-nums">{formatarMoeda(pedido.total)}</span>
            </Campo>
            <Campo rotulo="Responsável">{pedido.responsavelNome ?? '—'}</Campo>
            {pedido.cancelado && pedido.canceladoMotivo && (
              <Campo rotulo="Motivo do cancelamento" className="sm:col-span-2 lg:col-span-3">
                <span className="whitespace-pre-wrap">{pedido.canceladoMotivo}</span>
              </Campo>
            )}
            {pedido.observacoes && (
              <Campo rotulo="Observações" className="sm:col-span-2 lg:col-span-3">
                <span className="whitespace-pre-wrap">{pedido.observacoes}</span>
              </Campo>
            )}
          </dl>
        </CardContent>
      </Card>

      <Tabs value={aba} onValueChange={trocarAba} className="gap-4">
        <TabsList aria-label="Seções do pedido de compra">
          {ABAS_DETALHE_PEDIDO.map((valor) => (
            <TabsTrigger key={valor} value={valor} className="min-h-9 px-4">
              {ROTULOS_ABA[valor]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="itens">
          <ItensAba pedido={pedido} acoes={acoesItem} />
        </TabsContent>
        <TabsContent value="nfs">
          <NfsAba pedido={pedido} />
        </TabsContent>
        <TabsContent value="historico">
          <HistoricoAba pedidoId={pedido.id} versao={pedido.versao} />
        </TabsContent>
      </Tabs>

      {podeGerenciar && !pedidoCancelado && (
        <>
          <CancelarPedidoDialog aberto={cancelarAberto} onAbertoChange={setCancelarAberto} pedido={pedido} onSucesso={setPedido} onFalha={setToastFalha} />
          {itemParaEncerrar && (
            <EncerrarSaldoDialog
              aberto
              onAbertoChange={(aberto) => {
                if (!aberto) setItemEncerrando(null);
              }}
              pedidoId={pedido.id}
              item={itemParaEncerrar}
              onSucesso={setPedido}
              onFalha={setToastFalha}
            />
          )}
        </>
      )}

      {toastFalha && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-150"
        >
          <AlertTriangle className="size-4 shrink-0 text-amber-400" aria-hidden="true" />
          <span className="text-xs font-semibold">{toastFalha}</span>
        </div>
      )}
    </div>
  );
}
